import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, ExternalLink, RefreshCw, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import type { QuoteStatus } from '../api/types';

interface EmailDraftDialogProps {
  subject: string;
  body: string;
  clientEmail?: string | null;
  quoteStatus: QuoteStatus;
  onRegenerate: () => void;
  isRegenerating: boolean;
  onStatusChange?: (status: QuoteStatus) => Promise<void>;
  onClose: () => void;
  onSendEmail?: (to: string) => Promise<void>;
  isSending?: boolean;
}

function buildMailtoUrl(email: string | null | undefined, subject: string, body: string): string {
  const to = email ?? '';
  const truncatedBody = body.length > 2000 ? body.slice(0, 2000) + '...' : body;
  const params = new URLSearchParams();
  params.set('subject', subject);
  params.set('body', truncatedBody);
  return `mailto:${to}?${params.toString()}`;
}

import { isValidEmail } from '@/lib/validation';

export function EmailDraftDialog({
  subject,
  body,
  clientEmail,
  quoteStatus,
  onRegenerate,
  isRegenerating,
  onStatusChange,
  onClose,
  onSendEmail,
  isSending,
}: Readonly<EmailDraftDialogProps>) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [to, setTo] = useState(clientEmail ?? '');

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      setIsCopied(true);
      toast.success(t('email.copied'));
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error(t('email.error'));
    }
  }, [subject, body, t]);

  const handleOpenEmailClient = useCallback(async () => {
    const url = buildMailtoUrl(clientEmail, subject, body);
    globalThis.open(url, '_blank');

    if (quoteStatus === 'draft' && onStatusChange) {
      try {
        await onStatusChange('sent');
      } catch {
        toast.error(t('email.error'));
      }
    }
  }, [clientEmail, subject, body, quoteStatus, onStatusChange, t]);

  return (
    <div className="space-y-4 p-4" data-testid="email-draft-dialog">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t('email.title')}</h3>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          ✕
        </Button>
      </div>

      {onSendEmail && (
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('email.recipientEmail')}</label>
          <Input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={t('email.enterEmail')}
            data-testid="email-to-input"
          />
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('email.subject')}</label>
        <Input value={subject} readOnly data-testid="email-subject" />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t('email.body')}</label>
        <textarea
          value={body}
          readOnly
          className="w-full min-h-[200px] rounded-md border border-input bg-background px-3 py-2 text-sm"
          data-testid="email-body"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {onSendEmail && (
          <Button
            onClick={() => onSendEmail(to)}
            disabled={!isValidEmail(to) || isSending}
            data-testid="email-send-btn"
          >
            {isSending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-1 h-4 w-4" />
            )}
            {isSending ? t('email.sending') : t('email.sendNow')}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handleCopy}
          disabled={isCopied}
          data-testid="email-copy-btn"
        >
          <Copy className="mr-1 h-4 w-4" />
          {t('email.copy')}
        </Button>
        <Button onClick={handleOpenEmailClient} data-testid="email-open-btn">
          <ExternalLink className="mr-1 h-4 w-4" />
          {t('email.openClient')}
        </Button>
        <Button
          variant="outline"
          onClick={onRegenerate}
          disabled={isRegenerating}
          data-testid="email-regenerate-btn"
        >
          <RefreshCw className={`mr-1 h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
          {t('email.regenerate')}
        </Button>
      </div>
    </div>
  );
}

export { buildMailtoUrl };
